import type { CatalogFolder, CatalogFolderKind, CatalogItem } from "@calcplan/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Boxes,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CornerDownRight,
  Database,
  Folder,
  FolderOpen,
  FolderPlus,
  GripVertical,
  Image,
  PackagePlus,
  Palette,
  Pencil,
  Plus,
  Search,
  Settings2,
  Tags,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiAssetUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import type {
  BuilderCatalogCategory,
  BuilderCatalogDefinition,
} from "../module-builder/catalog";
import type { BuilderType } from "../module-builder/model";
import {
  CATALOG_MODULE_FILE_ACCEPT,
  catalogModuleFileFormat,
  catalogModuleImportEndpoint,
} from "./catalog-module-import";
import {
  buildCatalogRows,
  countCatalogFolders,
  filterCatalogTree,
  nextFolderSortOrder,
  resolveFolderDrop,
  resolveItemDrop,
  UNFILED_FOLDER_KEY,
  type CatalogDropPlacement,
  type CatalogFolderPatch,
  type CatalogItemPatch,
} from "./catalog-tree";

type AdminSection = "overview" | "statuses" | "catalog" | "clients" | "labels";
/**
 * Modules and materials live in the same library, and a category says which of
 * the two it holds. Everything the tree does — drag, rename, search, publish —
 * is the same for both; what changes is what "add" opens.
 */
const folderKindOf = (folder: { kind?: CatalogFolderKind } | undefined): CatalogFolderKind =>
  folder?.kind === "material" ? "material" : "module";
const FOLDER_KIND_LABEL: Record<CatalogFolderKind, string> = { module: "Módulos", material: "Materiais" };
type Status = { id: string; name: string; color: string; isDefault?: boolean; isSystem?: boolean };
type Client = { _id: string; name: string; email?: string | null; phone?: string | null };
type CatalogEntry = CatalogItem;
type CatalogViewStatus = "active" | "published" | "draft" | "retired";
type CatalogFolderScope = "direct" | "subtree";
type LabelTemplate = {
  _id: string;
  name: string;
  widthMm: number;
  heightMm: number;
  revision: number;
  fields: Array<{ key: string; xMm: number; yMm: number; fontSizePt: number; source: string }>;
};

const MAX_SKP_FILE_BYTES = 100 * 1024 * 1024;
const MAX_SKP_FILE_LABEL = "100 MB";
const MAX_SKM_FILE_BYTES = 40 * 1024 * 1024;
const MAX_SKM_FILE_LABEL = "40 MB";

const sections = [
  { id: "overview", label: "Visão geral", icon: Database },
  { id: "statuses", label: "Status de projeto", icon: ClipboardList },
  { id: "catalog", label: "Catálogo", icon: Boxes },
  { id: "clients", label: "Clientes", icon: Users },
  { id: "labels", label: "Etiquetas", icon: Tags },
] as const;

export function AdminPage() {
  const { session } = useAuth();
  const [section, setSection] = useState<AdminSection>("catalog");
  const isTenantAdmin = Boolean(session?.roles.includes("tenant_admin"));
  const availableSections = isTenantAdmin
    ? sections
    : sections.filter((entry) => entry.id === "catalog");
  return (
    <div className="page admin-page">
      <header className="page-header page-header--compact">
        <div><span className="page-kicker">Workspace · Administração</span><h1>Central administrativa</h1><p>Gerencie cadastros, catálogo e documentação técnica.</p></div>
      </header>
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Seções administrativas">
          {availableSections.map(({ id, label, icon: Icon }) => <button key={id} className={section === id ? "active" : ""} onClick={() => setSection(id)}><Icon size={18} /><span>{label}</span></button>)}
        </nav>
        <section className="settings-content">
          {section === "overview" && <Overview />}
          {section === "statuses" && <Statuses />}
          {section === "catalog" && <Catalog />}
          {section === "clients" && <Clients />}
          {section === "labels" && <Labels />}
        </section>
      </div>
    </div>
  );
}

function Overview() {
  const query = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => api<{ data: { users: number; projects: number; activeJobs: number; assets: { count: number; bytes: number }; recentAudit: Array<Record<string, unknown>> } }>("/admin/overview"),
  });
  const value = query.data?.data;
  return (
    <>
      <Heading icon={Database} title="Saúde do workspace" text="Indicadores operacionais e trilha de auditoria." />
      <div className="admin-metrics">
        <Metric label="Usuários ativos" value={value?.users ?? "—"} />
        <Metric label="Projetos" value={value?.projects ?? "—"} />
        <Metric label="Jobs em execução" value={value?.activeJobs ?? "—"} />
        <Metric label="Ativos" value={value ? `${value.assets.count} · ${formatBytes(value.assets.bytes)}` : "—"} />
      </div>
      <div className="settings-card">
        <h3>Auditoria recente</h3>
        <div className="admin-list">
          {value?.recentAudit.map((entry, index) => <div key={String(entry._id ?? index)}><span><strong>{String(entry.action ?? "evento")}</strong><small>{String(entry.outcome ?? "success")}</small></span><time>{entry.createdAt ? new Date(String(entry.createdAt)).toLocaleString("pt-BR") : "—"}</time></div>)}
          {!value?.recentAudit.length && <p className="muted">Nenhum evento registrado.</p>}
        </div>
      </div>
    </>
  );
}

function Statuses() {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6366F1");
  const query = useQuery({ queryKey: ["statuses"], queryFn: () => api<{ data: Status[] }>("/project-statuses") });
  const create = useMutation({
    mutationFn: () => api("/project-statuses", { method: "POST", body: { name, color } }),
    onSuccess: async () => { setName(""); await client.invalidateQueries({ queryKey: ["statuses"] }); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Status> }) => api(`/project-statuses/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["statuses"] }),
  });
  return (
    <>
      <Heading icon={ClipboardList} title="Status de projeto" text="Personalize o fluxo mantendo os estados de sistema." />
      <div className="settings-card admin-create-row">
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Novo status" maxLength={80} />
        <input type="color" value={color} onChange={(event) => setColor(event.target.value)} aria-label="Cor do status" />
        <button className="button button--primary" onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}><Plus size={16} /> Adicionar</button>
      </div>
      <div className="settings-card admin-list">
        {query.data?.data.map((status) => <div key={status.id}><span className="admin-status-dot" style={{ background: status.color }} /><span><strong>{status.name}</strong><small>{status.isSystem ? "Sistema" : "Personalizado"}{status.isDefault ? " · padrão" : ""}</small></span>{!status.isDefault && <button className="text-button" onClick={() => update.mutate({ id: status.id, patch: { isDefault: true } })}>Definir padrão</button>}</div>)}
      </div>
      <MutationError error={create.error} />
    </>
  );
}

function Clients() {
  const client = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const query = useQuery({ queryKey: ["clients"], queryFn: () => api<{ data: Client[] }>("/clients") });
  const create = useMutation({
    mutationFn: () => api("/clients", { method: "POST", body: { ...form, email: form.email || null, phone: form.phone || null } }),
    onSuccess: async () => { setForm({ name: "", email: "", phone: "" }); await client.invalidateQueries({ queryKey: ["clients"] }); },
  });
  return (
    <>
      <Heading icon={Users} title="Clientes" text="Cadastros reutilizáveis nas informações dos projetos." />
      <div className="settings-card admin-create-grid">
        <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Nome do cliente" />
        <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="E-mail" />
        <input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="Telefone" />
        <button className="button button--primary" onClick={() => create.mutate()} disabled={!form.name.trim() || create.isPending}><Plus size={16} /> Cadastrar</button>
      </div>
      <div className="settings-card admin-list">
        {query.data?.data.map((entry) => <div key={entry._id}><span><strong>{entry.name}</strong><small>{[entry.email, entry.phone].filter(Boolean).join(" · ") || "Sem contato"}</small></span></div>)}
        {!query.data?.data.length && <p className="muted">Nenhum cliente cadastrado.</p>}
      </div>
      <MutationError error={create.error} />
    </>
  );
}

/** What a row is carrying while it is being dragged around the tree. */
type CatalogDragPayload = { kind: "folder" | "item"; id: string };
/** The row whose name is currently an input instead of a label. */
type CatalogRenameDraft = { kind: "folder" | "item"; id: string; value: string };
type CatalogAdminFolder = CatalogFolder & { itemCount?: number };
/** "1 material", "11 materiais" — o plural de material não é "materialis". */
const contarMateriais = (quantidade: number): string =>
  `${quantidade} ${quantidade === 1 ? "material" : "materiais"}`;

/** O que qualquer uma das origens de material devolve depois de cadastrar. */
interface MaterialImportResult {
  data: CatalogEntry[];
  skipped: string[];
  failed?: Array<{ file: string; reason: string }>;
}

interface BuilderConfigDraft {
  definition: BuilderCatalogDefinition;
  partsJson: string;
  hardwareJson: string;
}

const BUILDER_CATEGORY_LABELS: Record<BuilderCatalogCategory, string> = {
  structure: "Estrutura",
  "shelf-mobile": "Prateleira móvel",
  "shelf-fixed": "Prateleira fixa",
  drawer: "Gaveta",
  internal: "Interno",
  "door-hinged": "Porta de giro",
  "door-sliding": "Porta de correr",
  cladding: "Chapa / revestimento",
  "metalon-profile": "Perfil Metalon",
};

const BUILDER_TYPE_LABELS: Record<BuilderType, string> = {
  straight: "Reto",
  "internal-corner": "Canto interno",
  "external-corner": "Canto externo",
};

function moduleBuilderDraft(item: CatalogEntry): BuilderConfigDraft {
  const saved = item.manufacturing?.moduleBuilder as BuilderCatalogDefinition | undefined;
  const definition: BuilderCatalogDefinition = saved && typeof saved === "object"
    ? structuredClone(saved)
    : {
        id: item.stableItemId,
        name: item.name,
        category: "internal",
        active: true,
        compatibleBuilderTypes: ["straight", "internal-corner", "external-corner"],
        parts: [],
        hardware: [],
        requirements: {},
        preview: { color: item.color },
        source: "tenant",
        sourceCatalogItemId: item.id,
      };
  definition.id ||= item.stableItemId;
  definition.name ||= item.name;
  definition.parts ??= [];
  definition.hardware ??= [];
  definition.requirements ??= {};
  definition.formulas ??= {};
  definition.source = "tenant";
  definition.sourceCatalogItemId = item.id;
  return {
    definition,
    partsJson: JSON.stringify(definition.parts, null, 2),
    hardwareJson: JSON.stringify(definition.hardware, null, 2),
  };
}

function Catalog() {
  const client = useQueryClient();
  const [form, setForm] = useState({
    stableItemId: "",
    name: "",
    description: "",
    folderId: "",
    color: "#B99C7B",
    status: "published" as "draft" | "published",
  });
  const [skpFile, setSkpFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [fileInputRevision, setFileInputRevision] = useState(0);
  const [catalogSearch, setCatalogSearch] = useState("");
  /** Qual das duas bibliotecas está aberta. Módulos e materiais não se misturam. */
  const [catalogKind, setCatalogKind] = useState<CatalogFolderKind>("module");
  const [statusFilter, setStatusFilter] = useState<CatalogViewStatus>("active");
  const [importOpen, setImportOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    folderId: "",
    name: "",
    color: "#C7A878",
    // O .skm é o formato em que um acabamento circula, então é o padrão.
    source: "skm" as "skm" | "skp" | "color",
    status: "published" as "draft" | "published",
  });
  const [materialSkmFiles, setMaterialSkmFiles] = useState<File[]>([]);
  const [materialSkpFile, setMaterialSkpFile] = useState<File | null>(null);
  const [materialReport, setMaterialReport] = useState("");
  const [openFolders, setOpenFolders] = useState<ReadonlySet<string>>(new Set<string>());
  const [openFoldersSeeded, setOpenFoldersSeeded] = useState(false);
  const [renaming, setRenaming] = useState<CatalogRenameDraft | null>(null);
  const [newFolder, setNewFolder] = useState<{ parentId: string | null; name: string; kind: CatalogFolderKind } | null>(null);
  const [dragging, setDragging] = useState<CatalogDragPayload | null>(null);
  const [dropHint, setDropHint] = useState<{ key: string; placement: CatalogDropPlacement } | null>(null);
  const [builderItem, setBuilderItem] = useState<CatalogEntry | null>(null);
  const [builderConfig, setBuilderConfig] = useState<BuilderConfigDraft | null>(null);
  const [builderConfigError, setBuilderConfigError] = useState("");

  const foldersQuery = useQuery({
    queryKey: ["catalog-folders-admin"],
    queryFn: () => api<{ data: CatalogAdminFolder[] }>("/catalog-folders"),
  });
  const query = useQuery({ queryKey: ["catalog-admin"], queryFn: () => api<{ data: CatalogEntry[] }>("/catalog-items?status=all&limit=1000") });
  const folders = useMemo(() => foldersQuery.data?.data ?? [], [foldersQuery.data]);
  const items = useMemo(() => query.data?.data ?? [], [query.data]);

  const activeItems = useMemo(
    () => items.filter((item) => item.status === "published" || item.status === "draft"),
    [items],
  );
  const moduleFolders = useMemo(() => folders.filter((folder) => folderKindOf(folder) === "module"), [folders]);
  const materialFolders = useMemo(() => folders.filter((folder) => folderKindOf(folder) === "material"), [folders]);

  /*
    Módulo e material não se misturam na árvore: a aba escolhe qual das duas
    bibliotecas está aberta, e tudo abaixo dela — categorias, itens, contagem e
    o botão de adicionar — pertence só àquela.
  */
  const visibleFolders = catalogKind === "material" ? materialFolders : moduleFolders;
  const visibleFolderIds = useMemo(() => new Set(visibleFolders.map((folder) => folder.id)), [visibleFolders]);
  const knownFolderIds = useMemo(() => new Set(folders.map((folder) => folder.id)), [folders]);
  const kindItems = useMemo(
    () => items.filter((item) => {
      if (item.folderId && visibleFolderIds.has(item.folderId)) return true;
      // Órfão é o que perdeu a pasta — não o que está na pasta da outra aba.
      // Confundir os dois faria um material aparecer como "Sem categoria" na
      // aba de módulos, que é exatamente a mistura que a aba veio desfazer.
      const orphan = !item.folderId || !knownFolderIds.has(item.folderId);
      return orphan && catalogKind === "module";
    }),
    [items, visibleFolderIds, knownFolderIds, catalogKind],
  );
  const activeKindCount = useMemo(
    () => kindItems.filter((item) => item.status === "published" || item.status === "draft").length,
    [kindItems],
  );
  /** Counts always describe the live catalogue, never the current filter. */
  const counts = useMemo(
    () => countCatalogFolders(visibleFolders, activeItems.filter((item) => visibleFolderIds.has(item.folderId ?? ""))),
    [activeItems, visibleFolders, visibleFolderIds],
  );
  const listedItems = useMemo(
    () => kindItems.filter((item) => (statusFilter === "active"
      ? item.status === "published" || item.status === "draft"
      : item.status === statusFilter)),
    [kindItems, statusFilter],
  );
  const filtered = useMemo(() => filterCatalogTree({
    folders: visibleFolders,
    items: listedItems,
    search: catalogSearch,
    itemText: (item) => `${item.name} ${item.stableItemId} ${item.root} ${item.category}`,
  }), [catalogSearch, visibleFolders, listedItems]);
  /** A search opens whatever it needs to open, without losing what the user had open. */
  const visibleOpenFolders = catalogSearch.trim() ? filtered.openFolders : openFolders;
  const rows = useMemo(() => buildCatalogRows({
    folders: filtered.folders,
    items: filtered.items,
    openFolders: visibleOpenFolders,
    counts,
  }), [counts, filtered, visibleOpenFolders]);
  const childFolderCounts = useMemo(() => {
    const result = new Map<string, number>();
    for (const folder of visibleFolders) {
      if (!folder.parentId) continue;
      result.set(folder.parentId, (result.get(folder.parentId) ?? 0) + 1);
    }
    return result;
  }, [folders]);

  useEffect(() => {
    if (openFoldersSeeded || !folders.length) return;
    setOpenFolders(new Set(folders.filter((folder) => !folder.parentId).map((folder) => folder.id)));
    setOpenFoldersSeeded(true);
  }, [folders, openFoldersSeeded]);

  useEffect(() => {
    if (!form.folderId && moduleFolders.length) setForm((value) => ({ ...value, folderId: moduleFolders[0]!.id }));
  }, [moduleFolders, form.folderId]);

  useEffect(() => {
    if (!materialForm.folderId && materialFolders.length) {
      setMaterialForm((value) => ({ ...value, folderId: materialFolders[0]!.id }));
    }
  }, [materialFolders, materialForm.folderId]);

  useEffect(() => {
    if (!importOpen && !materialOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setImportOpen(false);
      setMaterialOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [importOpen, materialOpen]);

  const refreshCatalog = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["catalog-admin"] }),
      client.invalidateQueries({ queryKey: ["catalog-items"] }),
      client.invalidateQueries({ queryKey: ["catalog-folders"] }),
      client.invalidateQueries({ queryKey: ["catalog-folders-admin"] }),
    ]);
  };
  const createFolder = useMutation({
    mutationFn: (draft: { name: string; parentId: string | null; kind: CatalogFolderKind }) =>
      api<{ data: CatalogFolder }>("/catalog-folders", {
        method: "POST",
        body: {
          name: draft.name,
          parentId: draft.parentId,
          kind: draft.kind,
          sortOrder: nextFolderSortOrder(folders, draft.parentId),
        },
      }),
    onSuccess: async (result, draft) => {
      setNewFolder(null);
      setOpenFolders((current) => {
        const next = new Set(current);
        if (draft.parentId) next.add(draft.parentId);
        next.add(result.data.id);
        return next;
      });
      // The new category becomes the destination of whichever importer matches
      // what it holds, so creating it and filling it is one movement.
      if (draft.kind === "material") setMaterialForm((value) => ({ ...value, folderId: result.data.id }));
      else setForm((value) => ({ ...value, folderId: result.data.id }));
      await refreshCatalog();
    },
  });
  const updateFolder = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api(`/catalog-folders/${id}`, { method: "PATCH", body: patch }),
    onSuccess: refreshCatalog,
  });
  /**
   * A drop renumbers the whole destination row, so the patches go out one at a
   * time: the server checks each move against the names already in the folder
   * and two parallel writes would race that check.
   */
  const moveFolder = useMutation({
    mutationFn: async (patches: CatalogFolderPatch[]) => {
      for (const patch of patches) {
        await api(`/catalog-folders/${patch.id}`, {
          method: "PATCH",
          body: { parentId: patch.parentId, sortOrder: patch.sortOrder },
        });
      }
    },
    onSuccess: refreshCatalog,
  });
  /** Same idea for the modules: the folder is renumbered one document at a time. */
  const moveModule = useMutation({
    mutationFn: async (patches: CatalogItemPatch[]) => {
      for (const patch of patches) {
        await api(`/catalog-items/${patch.id}`, {
          method: "PATCH",
          body: { folderId: patch.folderId, sortOrder: patch.sortOrder },
        });
      }
    },
    onSuccess: refreshCatalog,
  });
  const deleteFolder = useMutation({
    mutationFn: (id: string) => api(`/catalog-folders/${id}`, { method: "DELETE" }),
    onSuccess: refreshCatalog,
  });
  const create = useMutation({
    mutationFn: async () => {
      if (!skpFile) throw new Error("Selecione o arquivo SKP ou GLB do módulo.");
      const sourceFormat = catalogModuleFileFormat(skpFile.name);
      if (!sourceFormat) throw new Error("Use um arquivo com extensão .skp ou .glb.");
      if (skpFile.size > MAX_SKP_FILE_BYTES) {
        throw new Error(`O arquivo do módulo deve ter no máximo ${MAX_SKP_FILE_LABEL}.`);
      }
      if (thumbnailFile && thumbnailFile.size > 8_000_000) throw new Error("A imagem deve ter no máximo 8 MB.");
      return api(catalogModuleImportEndpoint(sourceFormat), {
        method: "POST",
        body: {
          ...form,
          stableItemId: form.stableItemId.trim() || undefined,
          sourceFileName: skpFile.name,
          sourceBase64: await fileToBase64(skpFile),
          thumbnailFileName: thumbnailFile?.name,
          thumbnailMimeType: thumbnailFile?.type || undefined,
          thumbnailBase64: thumbnailFile ? await fileToBase64(thumbnailFile) : undefined,
        },
      });
    },
    onSuccess: async () => {
      setForm((value) => ({ ...value, stableItemId: "", name: "", description: "" }));
      setSkpFile(null);
      setThumbnailFile(null);
      setFileInputRevision((value) => value + 1);
      setImportOpen(false);
      if (form.folderId) setOpenFolders((current) => new Set([...current, form.folderId]));
      await refreshCatalog();
    },
  });
  const updateItem = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api(`/catalog-items/${id}`, { method: "PATCH", body: patch }),
    onSuccess: refreshCatalog,
  });

  const openBuilderConfig = (item: CatalogEntry) => {
    setBuilderItem(item);
    setBuilderConfig(moduleBuilderDraft(item));
    setBuilderConfigError("");
  };

  const closeBuilderConfig = () => {
    if (updateItem.isPending) return;
    setBuilderItem(null);
    setBuilderConfig(null);
    setBuilderConfigError("");
  };

  const saveBuilderConfig = async () => {
    if (!builderItem || !builderConfig) return;
    try {
      const parts = JSON.parse(builderConfig.partsJson) as unknown;
      const hardware = JSON.parse(builderConfig.hardwareJson) as unknown;
      if (!Array.isArray(parts)) throw new Error("Peças precisa ser uma lista JSON.");
      if (!Array.isArray(hardware)) throw new Error("Ferragens precisa ser uma lista JSON.");
      if (!builderConfig.definition.compatibleBuilderTypes.length) {
        throw new Error("Selecione ao menos um tipo de construtor compatível.");
      }
      const definition: BuilderCatalogDefinition = {
        ...builderConfig.definition,
        id: builderConfig.definition.id.trim(),
        name: builderConfig.definition.name.trim(),
        parts: parts as BuilderCatalogDefinition["parts"],
        hardware: hardware as BuilderCatalogDefinition["hardware"],
        source: "tenant",
        sourceCatalogItemId: builderItem.id,
      };
      if (!definition.id || !definition.name) throw new Error("ID e nome do item são obrigatórios.");
      await updateItem.mutateAsync({
        id: builderItem.id,
        patch: {
          manufacturing: {
            ...(builderItem.manufacturing ?? {}),
            moduleBuilder: definition,
          },
        },
      });
      setBuilderItem(null);
      setBuilderConfig(null);
      setBuilderConfigError("");
    } catch (error) {
      setBuilderConfigError(error instanceof Error ? error.message : "Não foi possível validar a configuração.");
    }
  };
  /**
   * One upload, every finish in the file. A supplier's library is a single SKP
   * with forty materials on it, and registering them one at a time is the
   * reason nobody registers them.
   */
  const createMaterials = useMutation<MaterialImportResult>({
    // As três origens devolvem a mesma forma, para o relatório não ter que
    // adivinhar qual delas respondeu.
    mutationFn: async (): Promise<MaterialImportResult> => {
      if (materialForm.source === "skm") {
        if (!materialSkmFiles.length) throw new Error("Selecione ao menos um arquivo .skm.");
        const grande = materialSkmFiles.find((file) => file.size > MAX_SKM_FILE_BYTES);
        if (grande) throw new Error(`${grande.name} passa de ${MAX_SKM_FILE_LABEL}.`);
        return api<MaterialImportResult>(
          "/catalog-items/import-skm",
          {
            method: "POST",
            body: {
              folderId: materialForm.folderId,
              status: materialForm.status,
              files: await Promise.all(materialSkmFiles.map(async (file) => ({
                fileName: file.name,
                contentBase64: await fileToBase64(file),
              }))),
            },
          },
        );
      }
      if (materialForm.source === "color") {
        return api<{ data: CatalogEntry }>("/catalog-items/material", {
          method: "POST",
          body: {
            folderId: materialForm.folderId,
            name: materialForm.name.trim(),
            color: materialForm.color,
            status: materialForm.status,
          },
        }).then((result) => ({ data: [result.data], skipped: [], failed: [] }));
      }
      if (!materialSkpFile) throw new Error("Selecione o arquivo SKP com as texturas.");
      if (materialSkpFile.size > MAX_SKP_FILE_BYTES) {
        throw new Error(`O arquivo SKP deve ter no máximo ${MAX_SKP_FILE_LABEL}.`);
      }
      return api<MaterialImportResult>("/catalog-items/import-skp-materials", {
        method: "POST",
        body: {
          folderId: materialForm.folderId,
          status: materialForm.status,
          sourceFileName: materialSkpFile.name,
          sourceBase64: await fileToBase64(materialSkpFile),
        },
      });
    },
    onSuccess: async (result) => {
      const registered = result.data.length;
      const repeated = result.skipped.length;
      // Arquivo que não abriu tem que aparecer com nome e motivo: subir quarenta
      // e ver "38 cadastrados" sem saber quais dois faltaram é pior que um erro.
      const broken = result.failed ?? [];
      const partes = [
        registered
          ? `${contarMateriais(registered)} cadastrado${registered > 1 ? "s" : ""}`
          : `Nada novo cadastrado`,
        repeated ? `${repeated} já existia${repeated > 1 ? "m" : ""}` : "",
        broken.length ? `não abriu: ${broken.map((entry) => `${entry.file} (${entry.reason})`).join("; ")}` : "",
      ].filter(Boolean);
      setMaterialReport(`${partes.join(" · ")}.`);
      setMaterialForm((value) => ({ ...value, name: "" }));
      setMaterialSkmFiles([]);
      setMaterialSkpFile(null);
      setFileInputRevision((value) => value + 1);
      if (materialForm.folderId) setOpenFolders((current) => new Set([...current, materialForm.folderId]));
      await refreshCatalog();
    },
  });

  const toggleFolder = (folderId: string) => setOpenFolders((current) => {
    const next = new Set(current);
    if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
    return next;
  });
  const openEveryFolder = () => setOpenFolders(new Set([...folders.map((folder) => folder.id), UNFILED_FOLDER_KEY]));
  const closeEveryFolder = () => setOpenFolders(new Set<string>());
  const startRename = (kind: "folder" | "item", id: string, value: string) => {
    setNewFolder(null);
    setRenaming({ kind, id, value });
  };
  const commitRename = () => {
    if (!renaming) return;
    const name = renaming.value.trim();
    const draft = renaming;
    setRenaming(null);
    if (!name) return;
    if (draft.kind === "folder") {
      if (folders.find((folder) => folder.id === draft.id)?.name === name) return;
      updateFolder.mutate({ id: draft.id, patch: { name } });
      return;
    }
    if (items.find((item) => item.id === draft.id)?.name === name) return;
    updateItem.mutate({ id: draft.id, patch: { name } });
  };
  const startFolderCreation = (parentId: string | null, kind: CatalogFolderKind = "module") => {
    setRenaming(null);
    // A subcategory never chooses: it holds what its parent holds.
    setNewFolder({ parentId, name: "", kind });
    if (parentId) setOpenFolders((current) => new Set([...current, parentId]));
  };
  const openImporter = (folderId?: string) => {
    const targetFolderId = folderId ?? form.folderId ?? moduleFolders[0]?.id;
    if (targetFolderId) setForm((value) => ({ ...value, folderId: targetFolderId }));
    setImportOpen(true);
  };
  const openMaterialDialog = (folderId?: string) => {
    const targetFolderId = folderId ?? materialForm.folderId ?? materialFolders[0]?.id;
    if (targetFolderId) setMaterialForm((value) => ({ ...value, folderId: targetFolderId }));
    setMaterialReport("");
    setMaterialOpen(true);
  };
  const folderOptionLabel = (folder: CatalogFolder) =>
    folder.parentId
      ? `${folder.path.join(" / ")} · subpasta`
      : `${folder.name} · diretamente na categoria`;

  const endDrag = () => {
    setDragging(null);
    setDropHint(null);
  };
  const startDrag = (payload: CatalogDragPayload) => (event: React.DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", payload.id);
    setDragging(payload);
  };
  /**
   * The top and bottom edges of a folder row reorder it among its siblings; the
   * middle drops the dragged folder inside.
   */
  const placementAt = (event: React.DragEvent<HTMLElement>): CatalogDropPlacement => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offset = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
    if (offset < 0.3) return "before";
    if (offset > 0.7) return "after";
    return "inside";
  };
  /** A module row has no inside, so its midpoint is the only frontier. */
  const itemPlacementAt = (event: React.DragEvent<HTMLElement>): CatalogDropPlacement => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offset = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
    return offset < 0.5 ? "before" : "after";
  };
  const showHint = (key: string, placement: CatalogDropPlacement) => setDropHint((current) =>
    current?.key === key && current.placement === placement ? current : { key, placement });
  const folderDragOver = (rowKey: string, folderId: string) => (event: React.DragEvent<HTMLElement>) => {
    if (!dragging) return;
    if (dragging.kind === "item") {
      if (!resolveItemDrop({ items, draggedId: dragging.id, folderId }).length) return;
      event.preventDefault();
      showHint(rowKey, "inside");
      return;
    }
    const placement = placementAt(event);
    if (!resolveFolderDrop(folders, dragging.id, folderId, placement).length) return;
    event.preventDefault();
    showHint(rowKey, placement);
  };
  const moveItem = (patches: ReturnType<typeof resolveItemDrop>, folderId: string) => {
    if (!patches.length) return;
    setOpenFolders((current) => new Set([...current, folderId]));
    moveModule.mutate(patches);
  };
  /** Dropped anywhere on a folder row, a module goes to the end of that folder. */
  const folderDrop = (folderId: string) => (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const payload = dragging;
    endDrag();
    if (!payload) return;
    if (payload.kind === "item") {
      moveItem(resolveItemDrop({ items, draggedId: payload.id, folderId }), folderId);
      return;
    }
    const placement = placementAt(event);
    const patches = resolveFolderDrop(folders, payload.id, folderId, placement);
    if (!patches.length) return;
    if (placement === "inside") setOpenFolders((current) => new Set([...current, folderId]));
    moveFolder.mutate(patches);
  };
  const itemDragOver = (rowKey: string, itemId: string, folderId: string | null) =>
    (event: React.DragEvent<HTMLElement>) => {
      if (dragging?.kind !== "item" || !folderId) return;
      const placement = itemPlacementAt(event);
      if (!resolveItemDrop({ items, draggedId: dragging.id, folderId, targetItemId: itemId, placement }).length) return;
      event.preventDefault();
      showHint(rowKey, placement);
    };
  /** Dropped on the edge of another module, it takes that module's place. */
  const itemDrop = (itemId: string, folderId: string | null) => (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const payload = dragging;
    const placement = itemPlacementAt(event);
    endDrag();
    if (payload?.kind !== "item" || !folderId) return;
    moveItem(
      resolveItemDrop({ items, draggedId: payload.id, folderId, targetItemId: itemId, placement }),
      folderId,
    );
  };
  const rootDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (dragging?.kind !== "folder") return;
    if (!resolveFolderDrop(folders, dragging.id, null, "after").length) return;
    event.preventDefault();
    showHint("root", "after");
  };
  const rootDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const payload = dragging;
    endDrag();
    if (payload?.kind !== "folder") return;
    const patches = resolveFolderDrop(folders, payload.id, null, "after");
    if (patches.length) moveFolder.mutate(patches);
  };

  const renameField = (
    <form
      className="catalog-tree-rename"
      onSubmit={(event) => { event.preventDefault(); commitRename(); }}
    >
      <input
        autoFocus
        value={renaming?.value ?? ""}
        onChange={(event) => setRenaming((current) => (current ? { ...current, value: event.target.value } : current))}
        onKeyDown={(event) => { if (event.key === "Escape") setRenaming(null); }}
        onBlur={commitRename}
      />
      <button type="submit" className="catalog-icon-button" title="Salvar nome"><Check size={15} /></button>
    </form>
  );

  const folderCreator = (parentId: string | null, depth: number) => (
    <form
      className="catalog-tree-row catalog-tree-row--creator"
      style={{ "--tree-depth": depth } as React.CSSProperties}
      onSubmit={(event) => {
        event.preventDefault();
        const name = newFolder?.name.trim();
        if (name) createFolder.mutate({ name, parentId, kind: newFolder?.kind ?? "module" });
      }}
    >
      <span className="catalog-tree-disclosure" />
      <span className="catalog-tree-grip" />
      <span className="catalog-tree-icon">{newFolder?.kind === "material" ? <Palette size={16} /> : <FolderPlus size={16} />}</span>
      <input
        autoFocus
        value={newFolder?.name ?? ""}
        placeholder={parentId ? "Nome da nova subcategoria" : "Nome da nova categoria"}
        onChange={(event) => setNewFolder((current) => (current ? { ...current, name: event.target.value } : current))}
        onKeyDown={(event) => { if (event.key === "Escape") setNewFolder(null); }}
      />
      {/* A aba já disse o que esta biblioteca guarda; a linha só confirma. */}
      <small className="catalog-tree-kind-note">
        Guarda {FOLDER_KIND_LABEL[newFolder?.kind ?? "module"].toLocaleLowerCase("pt-BR")}
      </small>
      <button type="submit" className="button button--primary" disabled={!newFolder?.name.trim() || createFolder.isPending}>
        {createFolder.isPending ? "Criando…" : "Criar"}
      </button>
      <button type="button" className="catalog-icon-button" title="Cancelar" onClick={() => setNewFolder(null)}><X size={15} /></button>
    </form>
  );

  return (
    <>
      <Heading icon={Boxes} title="Catálogo com engenharia" text="Organize a biblioteca: módulos vindos do SketchUp e os materiais e texturas que os cobrem." />

      <section className="settings-card catalog-tree-card">
        {/*
          A aba é o que separa as duas bibliotecas. Antes as categorias de
          material ficavam no meio das de módulo, na mesma lista.
        */}
        <div className="catalog-kind-tabs" role="tablist" aria-label="Tipo de biblioteca">
          <button
            role="tab"
            aria-selected={catalogKind === "module"}
            className={catalogKind === "module" ? "active" : ""}
            onClick={() => { setCatalogKind("module"); setNewFolder(null); setRenaming(null); }}
          >
            <Boxes size={16} /> Módulos
          </button>
          <button
            role="tab"
            aria-selected={catalogKind === "material"}
            className={catalogKind === "material" ? "active" : ""}
            onClick={() => { setCatalogKind("material"); setNewFolder(null); setRenaming(null); }}
          >
            <Palette size={16} /> Materiais
          </button>
        </div>
        <header className="catalog-tree-header">
          <div>
            <strong>Biblioteca</strong>
            <small>
              {activeKindCount} {catalogKind === "material" ? "materiais" : "módulos"} ativos
              em {visibleFolders.length} categorias e subcategorias · arraste para reordenar ou aninhar
            </small>
          </div>
          <div className="catalog-tree-header-actions">
            <button className="button button--secondary" onClick={() => startFolderCreation(null, catalogKind)}>
              <FolderPlus size={16} /> Nova categoria
            </button>
            {catalogKind === "material" ? (
              <button
                className="button button--primary"
                onClick={() => openMaterialDialog()}
                disabled={!materialFolders.length}
                title={materialFolders.length ? "Cadastrar material ou textura" : "Crie antes uma categoria de materiais"}
              >
                <Palette size={16} /> Adicionar material
              </button>
            ) : (
              <button className="button button--primary" onClick={() => openImporter()} disabled={!moduleFolders.length}>
                <Plus size={16} /> Adicionar módulo
              </button>
            )}
          </div>
        </header>

        <div className="catalog-tree-toolbar">
          <label className="catalog-search-field">
            <Search size={15} />
            <input value={catalogSearch} onChange={(event) => setCatalogSearch(event.target.value)} placeholder={catalogKind === "material" ? "Buscar categoria ou material…" : "Buscar categoria ou módulo…"} />
            {catalogSearch && <button title="Limpar busca" onClick={() => setCatalogSearch("")}><X size={14} /></button>}
          </label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as CatalogViewStatus)} aria-label="Filtrar por publicação">
            <option value="active">Ativos</option>
            <option value="published">Publicados</option>
            <option value="draft">Rascunhos</option>
            <option value="retired">Excluídos</option>
          </select>
          <div className="catalog-tree-expander">
            <button onClick={openEveryFolder}>Expandir tudo</button>
            <button onClick={closeEveryFolder}>Recolher tudo</button>
          </div>
        </div>

        <div className="catalog-tree" role="tree" aria-label="Categorias, subcategorias e módulos" aria-busy={moveFolder.isPending || moveModule.isPending}>
          {rows.map((row) => {
            if (row.kind === "unfiled") {
              return (
                <div key={row.key} className="catalog-tree-row catalog-tree-row--unfiled" style={{ "--tree-depth": 0 } as React.CSSProperties}>
                  <button className="catalog-tree-disclosure" onClick={() => toggleFolder(UNFILED_FOLDER_KEY)} aria-label={row.expanded ? "Recolher" : "Expandir"}>
                    {row.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <span className="catalog-tree-grip" />
                  <span className="catalog-tree-icon"><Boxes size={16} /></span>
                  <span className="catalog-tree-name catalog-tree-name--static">
                    <strong>Sem categoria</strong>
                    <small>Módulos cuja pasta foi removida — arraste-os para uma categoria</small>
                  </span>
                  <em className="catalog-tree-count">{row.count}</em>
                  <span className="catalog-tree-actions" />
                </div>
              );
            }

            if (row.kind === "folder") {
              const folder = row.folder;
              const isRenaming = renaming?.kind === "folder" && renaming.id === folder.id;
              const subfolders = childFolderCounts.get(folder.id) ?? 0;
              const removable = row.totalCount === 0 && subfolders === 0;
              const hint = dropHint?.key === row.key ? dropHint.placement : null;
              const kind = folderKindOf(folder);
              return (
                <Fragment key={row.key}>
                  <div
                    className={[
                      "catalog-tree-row",
                      "catalog-tree-row--folder",
                      kind === "material" ? "catalog-tree-row--material" : "",
                      hint ? `is-drop-${hint}` : "",
                      dragging?.kind === "folder" && dragging.id === folder.id ? "is-dragging" : "",
                    ].filter(Boolean).join(" ")}
                    style={{ "--tree-depth": row.depth } as React.CSSProperties}
                    draggable={!isRenaming}
                    onDragStart={startDrag({ kind: "folder", id: folder.id })}
                    onDragEnd={endDrag}
                    onDragOver={folderDragOver(row.key, folder.id)}
                    onDragLeave={() => setDropHint((current) => (current?.key === row.key ? null : current))}
                    onDrop={folderDrop(folder.id)}
                    role="treeitem"
                    aria-expanded={row.hasChildren ? row.expanded : undefined}
                  >
                    <button
                      className="catalog-tree-disclosure"
                      onClick={() => toggleFolder(folder.id)}
                      disabled={!row.hasChildren}
                      aria-label={row.expanded ? "Recolher" : "Expandir"}
                    >
                      {row.hasChildren && (row.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                    </button>
                    <span className="catalog-tree-grip" title="Arraste para mover ou reordenar"><GripVertical size={13} /></span>
                    <span className="catalog-tree-icon" title={`Guarda ${FOLDER_KIND_LABEL[kind].toLocaleLowerCase("pt-BR")}`}>
                      {kind === "material" ? <Palette size={16} /> : row.expanded ? <FolderOpen size={16} /> : <Folder size={16} />}
                    </span>
                    {isRenaming ? renameField : (
                      <button className="catalog-tree-name" onClick={() => toggleFolder(folder.id)} onDoubleClick={() => startRename("folder", folder.id, folder.name)}>
                        <strong>{folder.name}</strong>
                        <small>
                          {row.depth === 0
                            ? `Categoria de ${FOLDER_KIND_LABEL[kind].toLocaleLowerCase("pt-BR")}`
                            : `Dentro de ${folder.path.slice(0, -1).join(" / ")}`}
                        </small>
                      </button>
                    )}
                    <em className="catalog-tree-count" title={`${row.directCount} aqui · ${row.totalCount} contando as subcategorias`}>
                      {row.directCount}{row.totalCount === row.directCount ? "" : ` / ${row.totalCount}`}
                    </em>
                    <span className="catalog-tree-actions">
                      <button className="catalog-icon-button" title="Nova subcategoria aqui dentro" onClick={() => startFolderCreation(folder.id, kind)}><FolderPlus size={15} /></button>
                      {kind === "material" ? (
                        <button className="catalog-icon-button" title="Cadastrar material nesta pasta" onClick={() => openMaterialDialog(folder.id)}><Palette size={15} /></button>
                      ) : (
                        <button className="catalog-icon-button" title="Adicionar módulo nesta pasta" onClick={() => openImporter(folder.id)}><PackagePlus size={15} /></button>
                      )}
                      <button className="catalog-icon-button" title="Renomear" onClick={() => startRename("folder", folder.id, folder.name)}><Pencil size={15} /></button>
                      <button
                        className="catalog-icon-button catalog-icon-button--danger"
                        title={removable ? "Excluir esta pasta" : "Mova ou exclua o conteúdo antes de apagar a pasta"}
                        disabled={!removable || deleteFolder.isPending}
                        onClick={() => {
                          if (window.confirm(`Excluir a pasta “${folder.name}”?`)) deleteFolder.mutate(folder.id);
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </span>
                  </div>
                  {newFolder?.parentId === folder.id && folderCreator(folder.id, row.depth + 1)}
                </Fragment>
              );
            }

            const item = row.item;
            const isRenaming = renaming?.kind === "item" && renaming.id === item.id;
            const hint = dropHint?.key === row.key ? dropHint.placement : null;
            const isMaterial = item.type === "material";
            const tileMm = item.pbr?.mapping?.physicalSizeMm;
            return (
              <div
                key={row.key}
                className={[
                  "catalog-tree-row",
                  "catalog-tree-row--item",
                  hint ? `is-drop-${hint}` : "",
                  dragging?.kind === "item" && dragging.id === item.id ? "is-dragging" : "",
                ].filter(Boolean).join(" ")}
                style={{ "--tree-depth": row.depth } as React.CSSProperties}
                draggable={!isRenaming && item.status !== "retired"}
                onDragStart={startDrag({ kind: "item", id: item.id })}
                onDragEnd={endDrag}
                onDragOver={itemDragOver(row.key, item.id, row.folderId)}
                onDragLeave={() => setDropHint((current) => (current?.key === row.key ? null : current))}
                onDrop={itemDrop(item.id, row.folderId)}
                role="treeitem"
              >
                <span className="catalog-tree-disclosure" />
                <span className="catalog-tree-grip" title="Arraste para mudar a ordem ou de categoria"><GripVertical size={13} /></span>
                <span className="catalog-engineering-thumb" style={{ background: item.color }}>
                  {item.thumbnailAssetId
                    ? <img src={apiAssetUrl(item.thumbnailAssetId)} alt="" loading="lazy" />
                    : isMaterial ? null : <Boxes size={18} />}
                </span>
                {isRenaming ? renameField : (
                  <span className="catalog-tree-name catalog-tree-name--static">
                    <strong>{item.name}</strong>
                    {/*
                      Um material não tem largura, altura e profundidade — dizer
                      0 × 0 × 0 mm era descrever a peça errada. O que identifica
                      uma textura é o tamanho do ladrilho; o que identifica uma
                      cor é a própria cor.
                    */}
                    <small>
                      {isMaterial
                        ? item.thumbnailAssetId
                          ? `${item.stableItemId} · textura ${Math.round(tileMm?.[0] ?? 0)} × ${Math.round(tileMm?.[1] ?? 0)} mm`
                          : `${item.stableItemId} · cor ${item.color.toLocaleUpperCase("pt-BR")}`
                        : `${item.stableItemId} · ${Math.round(item.dimensionsMm.width)} × ${Math.round(item.dimensionsMm.height)} × ${Math.round(item.dimensionsMm.depth)} mm${item.engineering ? ` · ${item.engineering.componentCount} componentes` : ""}`}
                    </small>
                  </span>
                )}
                <em className={`catalog-status catalog-status--${item.status}`}>
                  {item.status === "published" ? "Publicado" : item.status === "draft" ? "Rascunho" : "Excluído"}
                </em>
                <span className="catalog-tree-actions">
                  {item.status === "retired" ? (
                    <button className="catalog-row-text-button" onClick={() => updateItem.mutate({ id: item.id, patch: { status: "draft" } })}>Restaurar</button>
                  ) : (
                    <>
                      <button
                        className="catalog-row-text-button"
                        title={item.status === "published" ? "Mover para rascunho" : "Publicar no editor"}
                        onClick={() => updateItem.mutate({ id: item.id, patch: { status: item.status === "published" ? "draft" : "published" } })}
                      >
                        {item.status === "published" ? "Ocultar" : "Publicar"}
                      </button>
                      {!isMaterial && (
                        <button
                          className="catalog-icon-button"
                          title="Configurar no Construtor de Módulos"
                          onClick={() => openBuilderConfig(item)}
                        >
                          <Settings2 size={15} />
                        </button>
                      )}
                      <button className="catalog-icon-button" title={isMaterial ? "Renomear material" : "Renomear módulo"} onClick={() => startRename("item", item.id, item.name)}><Pencil size={15} /></button>
                      <button
                        className="catalog-icon-button catalog-icon-button--danger"
                        title={isMaterial ? "Excluir material" : "Excluir módulo"}
                        onClick={() => {
                          if (window.confirm(`Excluir “${item.name}” do catálogo?`)) updateItem.mutate({ id: item.id, patch: { status: "retired" } });
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </span>
              </div>
            );
          })}

          {newFolder?.parentId === null && folderCreator(null, 0)}

          {dragging?.kind === "folder" && (
            <div
              className={dropHint?.key === "root" ? "catalog-tree-root-drop is-over" : "catalog-tree-root-drop"}
              onDragOver={rootDragOver}
              onDragLeave={() => setDropHint((current) => (current?.key === "root" ? null : current))}
              onDrop={rootDrop}
            >
              <CornerDownRight size={15} /> Solte aqui para virar uma categoria principal
            </div>
          )}

          {foldersQuery.isLoading || query.isLoading ? (
            <div className="catalog-empty-state"><strong>Carregando catálogo…</strong></div>
          ) : !rows.length && (
            <div className="catalog-empty-state">
              <span>{catalogKind === "material" ? <Palette size={22} /> : <Boxes size={22} />}</span>
              <strong>{catalogSearch ? "Nada encontrado" : catalogKind === "material" ? "Nenhum material ainda" : "Nenhum módulo ainda"}</strong>
              <p>
                {catalogSearch
                  ? "Tente limpar a busca ou alterar o filtro."
                  : catalogKind === "material"
                    ? "Crie uma categoria e suba os arquivos .skm dos seus acabamentos."
                    : "Crie uma categoria e importe o primeiro módulo."}
              </p>
              {!catalogSearch && (
                <button className="button button--primary" onClick={() => startFolderCreation(null, catalogKind)}><FolderPlus size={15} /> Nova categoria</button>
              )}
            </div>
          )}
        </div>

        <MutationError error={createFolder.error ?? updateFolder.error ?? moveFolder.error ?? moveModule.error ?? deleteFolder.error ?? updateItem.error ?? query.error} />
      </section>

      {builderItem && builderConfig && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeBuilderConfig(); }}>
          <div className="dialog catalog-builder-dialog" role="dialog" aria-modal="true" aria-labelledby="catalog-builder-title">
            <header>
              <div>
                <span className="eyebrow">Catálogo · fabricação</span>
                <h2 id="catalog-builder-title">Configurar no Construtor de Módulos</h2>
                <p>Defina como “{builderItem.name}” se comporta, é dimensionado e vira peças de produção.</p>
              </div>
              <button className="icon-button" onClick={closeBuilderConfig} disabled={updateItem.isPending} aria-label="Fechar"><X size={20} /></button>
            </header>

            <div className="catalog-builder-form">
              <section>
                <h3>Identidade e disponibilidade</h3>
                <div className="catalog-builder-form__grid">
                  <label><span>ID funcional</span><input value={builderConfig.definition.id} onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, id: event.target.value } })} /></label>
                  <label><span>Nome exibido</span><input value={builderConfig.definition.name} onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, name: event.target.value } })} /></label>
                  <label>
                    <span>Categoria</span>
                    <select value={builderConfig.definition.category} onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, category: event.target.value as BuilderCatalogCategory } })}>
                      {(Object.entries(BUILDER_CATEGORY_LABELS) as Array<[BuilderCatalogCategory, string]>).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="catalog-builder-toggle"><input type="checkbox" checked={builderConfig.definition.active} onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, active: event.target.checked } })} /><span>Disponível no construtor</span></label>
                </div>
                <fieldset>
                  <legend>Tipos compatíveis</legend>
                  <div className="catalog-builder-checks">
                    {(Object.entries(BUILDER_TYPE_LABELS) as Array<[BuilderType, string]>).map(([value, label]) => (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={builderConfig.definition.compatibleBuilderTypes.includes(value)}
                          onChange={(event) => {
                            const current = builderConfig.definition.compatibleBuilderTypes;
                            const compatibleBuilderTypes = event.target.checked ? [...current, value] : current.filter((entry) => entry !== value);
                            setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, compatibleBuilderTypes } });
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </section>

              <section>
                <h3>Fórmulas paramétricas</h3>
                <p className="catalog-builder-help">Variáveis aceitas incluem $BAY_WIDTH$, $BAY_HEIGHT$, $BAY_DEPTH$, $MODULE_WIDTH$, $BOARD_THICKNESS$ e offsets.</p>
                <div className="catalog-builder-form__grid catalog-builder-form__grid--formulas">
                  {(["width", "height", "depth", "x", "y", "z"] as const).map((key) => (
                    <label key={key}>
                      <span>{{ width: "Largura", height: "Altura", depth: "Profundidade", x: "Posição X", y: "Posição Y", z: "Posição Z" }[key]}</span>
                      <input
                        value={builderConfig.definition.formulas?.[key] ?? ""}
                        onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, formulas: { ...(builderConfig.definition.formulas ?? {}), [key]: event.target.value } } })}
                        placeholder="Sem fórmula específica"
                      />
                    </label>
                  ))}
                </div>
              </section>

              <section>
                <h3>Restrições de aplicação</h3>
                <div className="catalog-builder-form__grid">
                  {([[
                    "minimumWidthMm", "Largura mínima (mm)"],
                    ["minimumHeightMm", "Altura mínima (mm)"],
                    ["minimumDepthMm", "Profundidade mínima (mm)"],
                    ["maximumRecessMm", "Recuo máximo (mm)"],
                  ] as const).map(([key, label]) => (
                    <label key={key}>
                      <span>{label}</span>
                      <input
                        type="number"
                        min={0}
                        value={builderConfig.definition.requirements?.[key] ?? ""}
                        onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, requirements: { ...(builderConfig.definition.requirements ?? {}), [key]: event.target.value === "" ? undefined : Number(event.target.value) } } })}
                      />
                    </label>
                  ))}
                  <label>
                    <span>Apoio obrigatório</span>
                    <select
                      value={builderConfig.definition.requirements?.attachment ?? "none"}
                      onChange={(event) => setBuilderConfig({ ...builderConfig, definition: { ...builderConfig.definition, requirements: { ...(builderConfig.definition.requirements ?? {}), attachment: event.target.value as "left" | "right" | "both" | "none" } } })}
                    >
                      <option value="none">Nenhum</option><option value="left">Lado esquerdo</option><option value="right">Lado direito</option><option value="both">Ambos os lados</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="catalog-builder-recipes">
                <h3>Receitas de fabricação</h3>
                <p className="catalog-builder-help">As listas são versionadas com o item e congeladas no módulo aplicado ao projeto.</p>
                <div>
                  <label><span>Peças (JSON)</span><textarea rows={12} spellCheck={false} value={builderConfig.partsJson} onChange={(event) => setBuilderConfig({ ...builderConfig, partsJson: event.target.value })} /></label>
                  <label><span>Ferragens (JSON)</span><textarea rows={12} spellCheck={false} value={builderConfig.hardwareJson} onChange={(event) => setBuilderConfig({ ...builderConfig, hardwareJson: event.target.value })} /></label>
                </div>
              </section>
            </div>

            {builderConfigError && <p className="form-error">{builderConfigError}</p>}
            <MutationError error={updateItem.error} />
            <footer>
              <button className="button button--secondary" onClick={closeBuilderConfig} disabled={updateItem.isPending}>Cancelar</button>
              <button className="button button--primary" onClick={() => void saveBuilderConfig()} disabled={updateItem.isPending}>
                <Settings2 size={16} /> {updateItem.isPending ? "Validando e salvando…" : "Salvar configuração"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setImportOpen(false); }}>
          <div className="dialog catalog-import-dialog" role="dialog" aria-modal="true" aria-labelledby="catalog-import-title">
            <header>
              <div>
                <span className="eyebrow">Catálogo</span>
                <h2 id="catalog-import-title">Novo módulo 3D</h2>
                <p>Importe o arquivo diretamente para a categoria escolhida.</p>
              </div>
              <button className="icon-button" onClick={() => setImportOpen(false)} disabled={create.isPending} aria-label="Fechar"><X size={20} /></button>
            </header>
            <div className="catalog-skp-form">
              <label><span>Nome do módulo</span><input autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ex.: Balcão 3 portas" /></label>
              <label><span>ID estável (opcional)</span><input value={form.stableItemId} onChange={(event) => setForm({ ...form, stableItemId: event.target.value })} placeholder="Gerado automaticamente" /></label>
              <label className="catalog-skp-form__wide"><span>Descrição</span><textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Texto exibido junto ao módulo" rows={2} /></label>
              <label className="catalog-location-field">
                <span>Categoria / pasta de destino</span>
                <select value={form.folderId} onChange={(event) => setForm({ ...form, folderId: event.target.value })}>
                  {moduleFolders.map((folder) => <option key={folder.id} value={folder.id}>{folderOptionLabel(folder)}</option>)}
                </select>
                <small>
                  {moduleFolders.find((folder) => folder.id === form.folderId)?.parentId
                    ? "O módulo ficará diretamente nesta subcategoria."
                    : "O módulo ficará diretamente na categoria principal, sem subpasta."}
                </small>
              </label>
              <label><span>Publicação</span><select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as "draft" | "published" })}><option value="published">Publicado no editor</option><option value="draft">Rascunho</option></select></label>
              <label className="catalog-file-field">
                <span><Upload size={15} /> Arquivo do módulo (.skp ou .glb)</span>
                <input key={`skp-${fileInputRevision}`} type="file" accept={CATALOG_MODULE_FILE_ACCEPT} onChange={(event) => setSkpFile(event.target.files?.[0] ?? null)} />
                <small>{skpFile ? `${skpFile.name} · ${formatBytes(skpFile.size)}` : `Obrigatório · até ${MAX_SKP_FILE_LABEL}`}</small>
              </label>
              <label className="catalog-file-field">
                <span><Image size={15} /> Foto do módulo</span>
                <input key={`image-${fileInputRevision}`} type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setThumbnailFile(event.target.files?.[0] ?? null)} />
                <small>{thumbnailFile ? `${thumbnailFile.name} · ${formatBytes(thumbnailFile.size)}` : "Opcional; usa a miniatura incorporada do SKP quando disponível"}</small>
              </label>
              <label className="catalog-color-field"><span>Cor de reserva</span><input type="color" value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} /></label>
            </div>
            <MutationError error={create.error} />
            <footer>
              <button className="button button--secondary" onClick={() => setImportOpen(false)} disabled={create.isPending}>Cancelar</button>
              <button className="button button--primary" onClick={() => create.mutate()} disabled={!form.name.trim() || !form.folderId || !skpFile || create.isPending}>
                <Upload size={16} /> {create.isPending ? "Processando geometria e engenharia…" : "Importar módulo"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {materialOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setMaterialOpen(false); }}>
          <div className="dialog catalog-import-dialog" role="dialog" aria-modal="true" aria-labelledby="catalog-material-title">
            <header>
              <div>
                <span className="eyebrow">Catálogo</span>
                <h2 id="catalog-material-title">Novo material</h2>
                <p>Suba os arquivos .skm dos seus acabamentos — pode ser a pasta inteira de uma vez.</p>
              </div>
              <button className="icon-button" onClick={() => setMaterialOpen(false)} disabled={createMaterials.isPending} aria-label="Fechar"><X size={20} /></button>
            </header>
            <div className="catalog-skp-form">
              <label className="catalog-skp-form__wide">
                <span>De onde vem</span>
                <select
                  value={materialForm.source}
                  onChange={(event) => {
                    setMaterialReport("");
                    setMaterialForm({ ...materialForm, source: event.target.value as "skm" | "skp" | "color" });
                  }}
                >
                  <option value="skm">Arquivos SKM · um material por arquivo</option>
                  <option value="skp">Arquivo SKP · colhe as texturas de dentro de um modelo</option>
                  <option value="color">Cor sólida · nome e cor, sem arquivo</option>
                </select>
              </label>
              <label className="catalog-location-field">
                <span>Categoria / pasta de destino</span>
                <select value={materialForm.folderId} onChange={(event) => setMaterialForm({ ...materialForm, folderId: event.target.value })}>
                  {materialFolders.map((folder) => <option key={folder.id} value={folder.id}>{folderOptionLabel(folder)}</option>)}
                </select>
                <small>Só aparecem aqui as categorias que guardam materiais.</small>
              </label>
              <label><span>Publicação</span><select value={materialForm.status} onChange={(event) => setMaterialForm({ ...materialForm, status: event.target.value as "draft" | "published" })}><option value="published">Publicado no editor</option><option value="draft">Rascunho</option></select></label>
              {materialForm.source === "skm" ? (
                <label className="catalog-file-field catalog-skp-form__wide">
                  <span><Upload size={15} /> Arquivos de material (.skm)</span>
                  <input
                    key={`material-skm-${fileInputRevision}`}
                    type="file"
                    accept=".skm"
                    multiple
                    onChange={(event) => setMaterialSkmFiles(Array.from(event.target.files ?? []))}
                  />
                  <small>
                    {materialSkmFiles.length
                      ? `${materialSkmFiles.length} arquivo(s) · ${formatBytes(materialSkmFiles.reduce((total, file) => total + file.size, 0))}`
                      : `Pode escolher a pasta inteira de uma vez. Nome, cor, textura e tamanho do ladrilho vêm de dentro do arquivo · até ${MAX_SKM_FILE_LABEL} cada`}
                  </small>
                </label>
              ) : materialForm.source === "skp" ? (
                <label className="catalog-file-field catalog-skp-form__wide">
                  <span><Upload size={15} /> Modelo com texturas dentro (.skp)</span>
                  <input key={`material-skp-${fileInputRevision}`} type="file" accept=".skp,model/vnd.sketchup" onChange={(event) => setMaterialSkpFile(event.target.files?.[0] ?? null)} />
                  <small>
                    {materialSkpFile
                      ? `${materialSkpFile.name} · ${formatBytes(materialSkpFile.size)}`
                      : `Cada material encontrado dentro do modelo vira um material do catálogo · até ${MAX_SKP_FILE_LABEL}`}
                  </small>
                </label>
              ) : (
                <>
                  <label><span>Nome do material</span><input autoFocus value={materialForm.name} onChange={(event) => setMaterialForm({ ...materialForm, name: event.target.value })} placeholder="Ex.: Tinta Branco Frio" /></label>
                  <label className="catalog-color-field"><span>Cor</span><input type="color" value={materialForm.color} onChange={(event) => setMaterialForm({ ...materialForm, color: event.target.value })} /></label>
                </>
              )}
            </div>
            {materialReport && <p className="catalog-material-report">{materialReport}</p>}
            <MutationError error={createMaterials.error} />
            <footer>
              <button className="button button--secondary" onClick={() => setMaterialOpen(false)} disabled={createMaterials.isPending}>Fechar</button>
              <button
                className="button button--primary"
                onClick={() => { setMaterialReport(""); createMaterials.mutate(); }}
                disabled={
                  !materialForm.folderId
                  || createMaterials.isPending
                  || (materialForm.source === "skm"
                    ? !materialSkmFiles.length
                    : materialForm.source === "skp" ? !materialSkpFile : !materialForm.name.trim())
                }
              >
                {materialForm.source === "skm"
                  ? <><Upload size={16} /> {createMaterials.isPending ? "Lendo os arquivos…" : materialSkmFiles.length ? `Cadastrar ${contarMateriais(materialSkmFiles.length)}` : "Cadastrar materiais"}</>
                  : materialForm.source === "skp"
                    ? <><Upload size={16} /> {createMaterials.isPending ? "Lendo as texturas do modelo…" : "Cadastrar texturas do SKP"}</>
                    : <><Palette size={16} /> {createMaterials.isPending ? "Cadastrando…" : "Cadastrar cor"}</>}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function Labels() {
  const client = useQueryClient();
  const [name, setName] = useState("Etiqueta de peça");
  const query = useQuery({ queryKey: ["label-templates"], queryFn: () => api<{ data: LabelTemplate[] }>("/label-templates") });
  const create = useMutation({
    mutationFn: () => api("/label-templates", { method: "POST", body: { name, widthMm: 100, heightMm: 50, fields: [{ key: "part", label: "Peça", xMm: 6, yMm: 12, fontSizePt: 11, source: "part.name" }, { key: "dimensions", label: "Dimensões", xMm: 6, yMm: 25, fontSizePt: 8, source: "part.dimensions" }, { key: "material", label: "Material", xMm: 6, yMm: 38, fontSizePt: 8, source: "part.material" }] } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["label-templates"] }),
  });
  return (
    <>
      <Heading icon={Tags} title="Modelos de etiqueta" text="Campos e posições em milímetros para impressão reproduzível." />
      <div className="settings-card admin-create-row"><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Nome do modelo" /><button className="button button--primary" onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}><Plus size={16} /> Criar modelo 100 × 50</button></div>
      <div className="label-template-grid">
        {query.data?.data.map((template) => <article className="label-template-card" key={template._id}><div className="label-paper" style={{ aspectRatio: `${template.widthMm} / ${template.heightMm}` }}>{template.fields.map((field) => <span key={field.key} style={{ left: `${field.xMm}%`, top: `${field.yMm * 1.7}%`, fontSize: Math.max(8, field.fontSizePt) }}>{`{{${field.source}}}`}</span>)}</div><strong>{template.name}</strong><small>{template.widthMm} × {template.heightMm} mm · R{template.revision}</small><Link className="text-button" to={`/labels/${template._id}`}>Abrir designer</Link></article>)}
      </div>
      <MutationError error={create.error} />
    </>
  );
}

function Heading({ icon: Icon, title, text }: { icon: typeof Database; title: string; text: string }) {
  return <header className="settings-heading"><span><Icon size={21} /></span><div><h2>{title}</h2><p>{text}</p></div></header>;
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <div><small>{label}</small><strong>{value}</strong></div>;
}

function MutationError({ error }: { error: Error | null }) {
  if (!error) return null;
  return <p className="form-error">{error instanceof Error ? error.message : "Não foi possível concluir a operação."}</p>;
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1_024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}
